import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users, DollarSign, TrendingDown, CheckCircle, XCircle, Eye, RefreshCw,
  Search, Calculator, Wallet, Clock, Award, Settings, Save, Plus, Trash2,
  Edit3, Ban, Percent, Gift, FileDown, Tag,
} from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import Pagination from "../components/Pagination";
import { fmtBDT } from "../lib/format";
import "../admin-affiliate-premium.css";

const TABS = [
  { id: "applications", label: "Applications", icon: Users },
  { id: "affiliates", label: "Affiliates", icon: Award },
  { id: "performance", label: "Performance (Net Loss)", icon: TrendingDown },
  { id: "commissions", label: "Commissions", icon: DollarSign },
  { id: "settings", label: "Program Settings", icon: Settings },
];

export default function AffiliateProgram({ defaultTab = "applications" }) {
  const [tab, setTab] = useState(defaultTab);

  // Build "current + last 12 months" options for the monthly PDF picker
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
      opts.push({ v, label: i === 0 ? `${label} (current)` : label });
    }
    return opts;
  }, []);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [downloading, setDownloading] = useState(false);

  const downloadMonthlyPdf = async () => {
    setDownloading(true);
    try {
      const params = selectedMonth ? { month: selectedMonth } : {};
      const res = await api.get(`/admin/affiliates/monthly-report.pdf`, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const m = selectedMonth || (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      })();
      a.href = url;
      a.download = `affiliate-summary-${m}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="admin-affiliate-program" className="aff-premium">
      <PageHeader
        title="Affiliate Program"
        subtitle="Maxlive88-style affiliate — applications · net-loss performance · 15-day commissions · monthly history auto-clears on the 1st"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="h-9 px-2 border border-gray-200 rounded-sm text-[13px] bg-white"
              data-testid="aff-pdf-month-select"
              title="Select month for PDF report"
            >
              {monthOptions.map((o, i) => (
                <option key={o.v} value={i === 0 ? "" : o.v}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={downloadMonthlyPdf}
              disabled={downloading}
              className="h-9 px-3 inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-[13px] font-semibold disabled:opacity-50"
              data-testid="aff-download-monthly-pdf"
              title="One-click per-affiliate monthly summary PDF (referees · deposits · withdrawals · loss · commission paid + pending)"
            >
              <FileDown size={13} /> {downloading ? "Generating…" : "Download Monthly PDF"}
            </button>
          </div>
        }
      />

      <div className="flex items-center gap-1 border-b border-gray-200 mb-5">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 border-b-2 transition ${
              tab === t.id
                ? "border-emerald-500 text-emerald-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            data-testid={`aff-tab-${t.id}`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "applications" && <Applications />}
      {tab === "affiliates" && <Affiliates />}
      {tab === "performance" && <Performance />}
      {tab === "commissions" && <Commissions />}
      {tab === "settings" && <ProgramSettings />}
    </div>
  );
}

/* ============================ Applications ============================ */
function Applications() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [actioning, setActioning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/affiliate-applications", {
        params: { status: filter, search: search || undefined },
      });
      setRows(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    const notes = action === "reject" ? (window.prompt("Reason for rejection (optional)") || "") : "";
    if (action === "approve" && !window.confirm("Approve this affiliate application?")) return;
    setActioning(id);
    try {
      await api.patch(`/affiliate-applications/${id}`, { action, notes });
      toast.success(`Application ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setActioning(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Counts */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Pending" value={counts.pending || 0} color="amber" icon={Clock} />
        <StatCard label="Approved" value={counts.approved || 0} color="emerald" icon={CheckCircle} />
        <StatCard label="Rejected" value={counts.rejected || 0} color="rose" icon={XCircle} />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-sm p-3">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 h-8 text-xs uppercase tracking-wider font-semibold rounded-sm ${
              filter === s ? "bg-emerald-600 text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
            data-testid={`aff-app-filter-${s}`}
          >
            {s}
          </button>
        ))}
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search username, email, phone..."
            className="w-full h-8 pl-8 pr-3 border border-gray-300 rounded-sm text-sm"
          />
        </div>
        <button onClick={load} className="h-8 px-3 border border-gray-300 rounded-sm text-sm inline-flex items-center gap-1.5 hover:bg-gray-50">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">User</th>
              <th className="px-3 py-2 text-left font-semibold">Contact</th>
              <th className="px-3 py-2 text-left font-semibold">Traffic Source</th>
              <th className="px-3 py-2 text-left font-semibold">Promo Plan</th>
              <th className="px-3 py-2 text-left font-semibold">Submitted</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th>
              <th className="px-3 py-2 text-right font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-12 text-center text-gray-400">No applications</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{r.username}</div>
                  <div className="text-[11px] text-gray-500 font-mono">{r.user_id.slice(-8)}</div>
                  {r.panel_username && (
                    <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold">
                      🔑 @{r.panel_username}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-[12px] text-gray-700">
                  <div>{r.email}</div>
                  <div className="text-gray-500">{r.phone}</div>
                  {r.website && <div className="text-blue-600 text-[11px] truncate max-w-[200px]">{r.website}</div>}
                </td>
                <td className="px-3 py-2 text-[12px] text-gray-700">
                  <div>{r.traffic_source || "—"}</div>
                  {r.monthly_traffic && <div className="text-gray-500 text-[11px]">~{r.monthly_traffic}/mo</div>}
                </td>
                <td className="px-3 py-2 text-[12px] text-gray-600 max-w-[260px] truncate" title={r.promo_plan}>
                  {r.promo_plan || "—"}
                </td>
                <td className="px-3 py-2 text-[12px] text-gray-600 whitespace-nowrap">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2">
                  <StatusPill status={r.status} />
                  {r.notes && <div className="text-[10px] text-gray-500 mt-0.5 italic">“{r.notes}”</div>}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.status === "pending" ? (
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => act(r.id, "approve")}
                        disabled={actioning === r.id}
                        className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-sm text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`aff-approve-${r.id}`}
                      >
                        <CheckCircle size={11} /> Approve
                      </button>
                      <button
                        onClick={() => act(r.id, "reject")}
                        disabled={actioning === r.id}
                        className="px-2.5 py-1 bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 rounded-sm text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`aff-reject-${r.id}`}
                      >
                        <XCircle size={11} /> Reject
                      </button>
                    </div>
                  ) : (
                    <span className="text-[11px] text-gray-500">Reviewed</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================ Performance ============================ */
function Performance() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(15);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null); // { affiliate_id, items }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/affiliate-performance", { params: { period_days: days } });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load performance");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const openDrill = async (aff) => {
    try {
      const { data: res } = await api.get(`/affiliate-performance/${aff.affiliate_id}/users`, {
        params: { period_days: days },
      });
      setDrill({ affiliate: res.affiliate, items: res.items, commission_pct: res.commission_pct });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load breakdown");
    }
  };

  const [perfDownloading, setPerfDownloading] = useState(false);
  const downloadPerfPdf = async () => {
    setPerfDownloading(true);
    try {
      const res = await api.get(`/admin/affiliate-performance.pdf`, {
        params: { period_days: days },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `affiliate-performance-last-${days}d.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF download failed");
    } finally {
      setPerfDownloading(false);
    }
  };

  const [drillDownloading, setDrillDownloading] = useState(false);
  const downloadDrillPdf = async () => {
    if (!drill) return;
    setDrillDownloading(true);
    try {
      const res = await api.get(`/admin/affiliate-performance/${drill.affiliate.id}/users.pdf`, {
        params: { period_days: days },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `affiliate-${drill.affiliate.refer_code || "aff"}-players-last-${days}d.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF download failed");
    } finally {
      setDrillDownloading(false);
    }
  };

  if (!data) return <div className="p-10 text-center text-gray-500 text-sm">Loading...</div>;

  const totals = (data.items || []).reduce(
    (acc, r) => ({
      dep: acc.dep + r.deposits,
      wd: acc.wd + r.withdrawals,
      loss: acc.loss + r.net_loss,
      comm: acc.comm + r.commission,
      paid: acc.paid + (r.paid_out || 0),
    }),
    { dep: 0, wd: 0, loss: 0, comm: 0, paid: 0 }
  );

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total Deposits" value={fmtBDT(totals.dep)} color="blue" icon={Wallet} />
        <StatCard label="Total Withdrawals" value={fmtBDT(totals.wd)} color="amber" icon={Wallet} />
        <StatCard label="Net Loss (Players)" value={fmtBDT(totals.loss)} color="rose" icon={TrendingDown} />
        <StatCard label="Commission (Period)" value={fmtBDT(totals.comm)} color="emerald" icon={Award} />
        <StatCard label="Paid Out (Lifetime)" value={fmtBDT(totals.paid)} color="violet" icon={Award} />
      </div>

      {/* Period selector */}
      <div className="bg-white border border-gray-200 rounded-sm p-3 flex flex-wrap items-center gap-3">
        <span className="text-sm text-gray-700 font-semibold">Period:</span>
        {[7, 15, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-3 h-8 text-xs font-semibold rounded-sm ${
              days === d ? "bg-emerald-600 text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
            data-testid={`aff-perf-days-${d}`}
          >
            Last {d} days
          </button>
        ))}
        <span className="text-xs text-gray-500 ml-auto">
          Default rate: <b>{data.commission_pct}%</b>
          {data.tiers_enabled && <span className="ml-2 px-1.5 py-0.5 rounded-sm bg-purple-50 text-purple-700 border border-purple-200 text-[10px] font-semibold uppercase tracking-wider">Tiered</span>}
        </span>
        <button onClick={load} className="h-8 px-3 border border-gray-300 rounded-sm text-sm inline-flex items-center gap-1.5 hover:bg-gray-50">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <button
          onClick={downloadPerfPdf}
          disabled={perfDownloading || loading}
          className="h-8 px-3 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="aff-perf-export-pdf"
          title="Download Performance (Net Loss) summary as PDF"
        >
          <FileDown size={13} /> {perfDownloading ? "…" : "Export PDF"}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Affiliate</th>
              <th className="px-3 py-2 text-left font-semibold">Code</th>
              <th className="px-3 py-2 text-right font-semibold">Players</th>
              <th className="px-3 py-2 text-right font-semibold">Deposits</th>
              <th className="px-3 py-2 text-right font-semibold">Withdrawals</th>
              <th className="px-3 py-2 text-right font-semibold">Net Loss</th>
              <th className="px-3 py-2 text-center font-semibold">Rate · Tier</th>
              <th className="px-3 py-2 text-right font-semibold text-emerald-700">Commission</th>
              <th className="px-3 py-2 text-right font-semibold text-violet-700">Paid Out</th>
              <th className="px-3 py-2 text-right font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data.items || []).length === 0 && (
              <tr><td colSpan={10} className="px-3 py-12 text-center text-gray-400">No affiliates yet</td></tr>
            )}
            {(data.items || []).map((a) => (
              <tr key={a.affiliate_id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-gray-900">{a.username}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-gray-600">{a.refer_code}</td>
                <td className="px-3 py-2 text-right tabular-nums">{a.referred_count}</td>
                <td className="px-3 py-2 text-right tabular-nums text-blue-700">{fmtBDT(a.deposits)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmtBDT(a.withdrawals)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-rose-700">{fmtBDT(a.net_loss)}</td>
                <td className="px-3 py-2 text-center">
                  <div className="inline-flex flex-col items-center leading-tight">
                    <span className="font-bold text-[12px] text-gray-800">{Number(a.commission_pct ?? 0).toFixed(1)}%</span>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${a.has_custom_pct ? 'text-purple-600' : 'text-gray-500'}`}>
                      {a.tier_name || '—'}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-700">{fmtBDT(a.commission)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-violet-700">{fmtBDT(a.paid_out || 0)}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => openDrill(a)}
                    className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 rounded-sm text-[11px] font-semibold inline-flex items-center gap-1"
                    data-testid={`aff-drill-${a.affiliate_id}`}
                  >
                    <Eye size={11} /> Players
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drill-down modal */}
      {drill && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setDrill(null)}>
          <div className="bg-white rounded-sm max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-900">{drill.affiliate.username}</div>
                <div className="text-xs text-gray-500 font-mono">{drill.affiliate.refer_code} — last {days} days</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={downloadDrillPdf}
                  disabled={drillDownloading}
                  className="h-8 px-3 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="aff-drill-export-pdf"
                  title="Download player breakdown PDF"
                >
                  <FileDown size={12} /> {drillDownloading ? "…" : "Export PDF"}
                </button>
                <button onClick={() => setDrill(null)} className="p-1.5 hover:bg-gray-100 rounded-sm" data-testid="aff-drill-close"><XCircle size={18} className="text-gray-500" /></button>
              </div>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">User</th>
                    <th className="px-3 py-2 text-left font-semibold">Phone</th>
                    <th className="px-3 py-2 text-left font-semibold">Promo/Code Used</th>
                    <th className="px-3 py-2 text-left font-semibold">Joined</th>
                    <th className="px-3 py-2 text-right font-semibold">Balance</th>
                    <th className="px-3 py-2 text-right font-semibold">Deposits</th>
                    <th className="px-3 py-2 text-right font-semibold">Withdrawals</th>
                    <th className="px-3 py-2 text-right font-semibold">Net Loss</th>
                    <th className="px-3 py-2 text-right font-semibold text-emerald-700">Contribution ({drill.commission_pct}%)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {drill.items.length === 0 && (
                    <tr><td colSpan={9} className="px-3 py-12 text-center text-gray-400">No players yet</td></tr>
                  )}
                  {drill.items.map((u) => (
                    <tr key={u.user_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">{u.username}</td>
                      <td className="px-3 py-2 text-[12px] text-gray-600">{u.phone || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-indigo-700">{u.promo_code_used || "—"}</td>
                      <td className="px-3 py-2 text-[11px] text-gray-500">{u.joined_at ? new Date(u.joined_at).toLocaleDateString() : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtBDT(u.balance)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-blue-700">{fmtBDT(u.deposits)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmtBDT(u.withdrawals)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-700 font-semibold">{fmtBDT(u.net_loss)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-bold">{fmtBDT(u.commission_contribution)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================ Commissions ============================ */
function Commissions() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/affiliate-commissions", { params: { status } });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load commissions");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const runCalc = async () => {
    if (!window.confirm("Calculate commissions for the previous completed period? Existing records will be skipped.")) return;
    setCalculating(true);
    try {
      const { data: res } = await api.post("/affiliate-commissions/calculate", {});
      const extra = (res.sub_created || 0) > 0 ? `, +${res.sub_created} sub-override` : "";
      toast.success(`Created ${res.created} record(s)${extra}, skipped ${res.skipped}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calculation failed");
    } finally {
      setCalculating(false);
    }
  };

  const pay = async (id, amount, username) => {
    if (!window.confirm(`Pay ${fmtBDT(amount)} commission to ${username}? The amount will be credited to their balance.`)) return;
    try {
      await api.post(`/affiliate-commissions/${id}/pay`);
      toast.success(`Paid ${fmtBDT(amount)} to ${username}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    }
  };

  if (!data) return <div className="p-10 text-center text-gray-500 text-sm">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatCard label="Pending Commissions" value={fmtBDT(data.total_pending)} color="amber" icon={Clock} />
        <StatCard label="Total Paid" value={fmtBDT(data.total_paid)} color="emerald" icon={CheckCircle} />
      </div>

      <div className="bg-white border border-gray-200 rounded-sm p-3 flex flex-wrap items-center gap-2">
        {["all", "pending", "paid"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 h-8 text-xs uppercase tracking-wider font-semibold rounded-sm ${
              status === s ? "bg-emerald-600 text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            {s}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <button onClick={load} className="h-8 px-3 border border-gray-300 rounded-sm text-sm inline-flex items-center gap-1.5 hover:bg-gray-50">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={runCalc}
            disabled={calculating}
            className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="aff-comm-calc"
          >
            <Calculator size={13} /> {calculating ? "Calculating..." : "Calculate Previous Period"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Affiliate</th>
              <th className="px-3 py-2 text-center font-semibold">Type</th>
              <th className="px-3 py-2 text-left font-semibold">Period</th>
              <th className="px-3 py-2 text-right font-semibold">Refs</th>
              <th className="px-3 py-2 text-right font-semibold">Deposits</th>
              <th className="px-3 py-2 text-right font-semibold">Net Loss</th>
              <th className="px-3 py-2 text-right font-semibold text-emerald-700">Commission</th>
              <th className="px-3 py-2 text-center font-semibold">Status</th>
              <th className="px-3 py-2 text-right font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data.items || []).length === 0 && (
              <tr><td colSpan={9} className="px-3 py-12 text-center text-gray-400">
                No commission records. Click <b>Calculate Previous Period</b> to generate.
              </td></tr>
            )}
            {(data.items || []).map((c) => {
              const isSub = c.type === "sub_override";
              return (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">
                  {c.affiliate_username}
                  {isSub && (
                    <div className="text-[10px] text-purple-600 mt-0.5">
                      ↳ from downline <b>{c.downline_username}</b>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {isSub ? (
                    <span className="inline-flex items-center px-2 py-0.5 bg-purple-50 border border-purple-200 text-purple-700 rounded-sm text-[10px] font-bold uppercase tracking-wider">
                      Sub-Override
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-sm text-[10px] font-bold uppercase tracking-wider">
                      RevShare
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-[12px] text-gray-600 whitespace-nowrap">
                  {c.period_start?.slice(0, 10)} → {c.period_end?.slice(0, 10)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{isSub ? "—" : c.referred_users_count}</td>
                <td className="px-3 py-2 text-right tabular-nums text-blue-700">
                  {isSub ? fmtBDT(c.downline_commission_amount) : fmtBDT(c.total_deposits)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-700">{isSub ? "—" : fmtBDT(c.net_loss)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-700">
                  {fmtBDT(c.commission_amount)}
                  <div className="text-[10px] text-gray-500">{Number(c.commission_rate).toFixed(1)}%</div>
                </td>
                <td className="px-3 py-2 text-center"><StatusPill status={c.status} /></td>
                <td className="px-3 py-2 text-right">
                  {c.status === "pending" ? (
                    <button
                      onClick={() => pay(c.id, c.commission_amount, c.affiliate_username)}
                      className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-[11px] font-semibold inline-flex items-center gap-1"
                      data-testid={`aff-pay-${c.id}`}
                    >
                      <DollarSign size={11} /> Pay
                    </button>
                  ) : (
                    <span className="text-[11px] text-gray-500">
                      {c.paid_at ? new Date(c.paid_at).toLocaleDateString() : "—"}
                    </span>
                  )}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================ Affiliates List (full management) ============================ */
function Affiliates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // affiliate object being edited
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/affiliate-list", { params: { search: search || undefined } });
      setRows(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load affiliates");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    try {
      await api.patch(`/affiliate-list/${editing.affiliate_id}`, payload);
      toast.success("Affiliate updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const revoke = async (aff) => {
    if (!window.confirm(`Revoke ${aff.username}'s affiliate status? Their referral code will stop earning commissions.`)) return;
    try {
      await api.patch(`/affiliate-list/${aff.affiliate_id}`, { revoke: true });
      toast.success("Affiliate revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const quickToggle = async (aff, field) => {
    const verb = aff[field] ? "Un-" : "";
    const labels = { account_blocked: "Block login", balance_frozen: "Freeze balance", payout_blocked: "Block payout" };
    const action = `${verb}${labels[field]}`;
    if (!window.confirm(`${action} for ${aff.username}?`)) return;
    const map = { account_blocked: "block_login", balance_frozen: "freeze_balance", payout_blocked: "block_payout" };
    try {
      await api.patch(`/affiliate-list/${aff.affiliate_id}`, { [map[field]]: !aff[field] });
      toast.success(`${action} done`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const hardDelete = async (aff) => {
    if (!window.confirm(`PERMANENTLY DELETE the user account for ${aff.username}?\n\nThis is irreversible. Their commissions and referral history will remain in records, but the user will no longer be able to log in.`)) return;
    if (!window.confirm(`Final confirmation — delete ${aff.username} forever?`)) return;
    try {
      await api.patch(`/affiliate-list/${aff.affiliate_id}`, { delete_account: true });
      toast.success("Affiliate account deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const totals = rows.reduce((acc, r) => ({
    refs: acc.refs + r.referred_count,
    dep: acc.dep + r.lifetime_deposits,
    net: acc.net + r.lifetime_net_loss,
    paid: acc.paid + r.lifetime_paid_commission + r.lifetime_cpa_paid,
  }), { refs: 0, dep: 0, net: 0, paid: 0 });

  // Newest-first ordering + pagination (client-side after server-side search)
  const sortedRows = useMemo(() => {
    const arr = Array.isArray(rows) ? [...rows] : [];
    arr.sort((a, b) => String(b?.created_at || "").localeCompare(String(a?.created_at || "")));
    return arr;
  }, [rows]);
  useEffect(() => { setPage(1); }, [search, sortedRows.length]);
  const visibleRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE);
  }, [sortedRows, page]);

  return (
    <div className="space-y-4" data-testid="affiliates-list-section">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Active Affiliates" value={rows.length} color="blue" icon={Users} />
        <StatCard label="Total Referrals" value={totals.refs} color="emerald" icon={Users} />
        <StatCard label="Lifetime Net Loss" value={fmtBDT(totals.net)} color="rose" icon={TrendingDown} />
        <StatCard label="Lifetime Paid Out" value={fmtBDT(totals.paid)} color="amber" icon={Award} />
      </div>

      <div className="bg-white border border-gray-200 rounded-sm p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search username, email, refer code..."
            className="w-full h-8 pl-8 pr-3 border border-gray-300 rounded-sm text-sm"
            data-testid="aff-list-search"
          />
        </div>
        <button onClick={load} className="h-8 px-3 border border-gray-300 rounded-sm text-sm inline-flex items-center gap-1.5 hover:bg-gray-50">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Affiliate</th>
              <th className="px-3 py-2 text-left font-semibold">Code</th>
              <th className="px-3 py-2 text-left font-semibold">Upline</th>
              <th className="px-3 py-2 text-right font-semibold">Refs</th>
              <th className="px-3 py-2 text-right font-semibold">Lifetime Dep.</th>
              <th className="px-3 py-2 text-right font-semibold">Net Loss</th>
              <th className="px-3 py-2 text-right font-semibold">Paid Out</th>
              <th className="px-3 py-2 text-center font-semibold">Custom %</th>
              <th className="px-3 py-2 text-center font-semibold">Status</th>
              <th className="px-3 py-2 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedRows.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-12 text-center text-gray-400">No approved affiliates yet</td></tr>
            )}
            {visibleRows.map((r) => (
              <tr key={r.affiliate_id} className="hover:bg-gray-50" data-testid={`aff-row-${r.affiliate_id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{r.username}</div>
                  <div className="text-[11px] text-gray-500">{r.email || r.phone || "—"}</div>
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-gray-700">
                  {r.refer_code || "—"}
                  {r.promo_code && (
                    <div className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-300 text-emerald-700 rounded-sm font-bold text-[10px]" title="Custom promo code">
                      <Tag size={9} /> {r.promo_code}
                    </div>
                  )}
                  {r.downline_count > 0 && (
                    <div className="text-[10px] text-purple-700 font-semibold">{r.downline_count} downline{r.downline_count > 1 ? "s" : ""}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-[12px]">
                  {r.parent_username ? (
                    <div>
                      <div className="font-semibold text-purple-700">{r.parent_username}</div>
                      <div className="text-[10px] text-gray-500 font-mono">{r.parent_refer_code}</div>
                    </div>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{r.referred_count}</td>
                <td className="px-3 py-2 text-right tabular-nums text-blue-700">{fmtBDT(r.lifetime_deposits)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-700 font-semibold">{fmtBDT(r.lifetime_net_loss)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                  {fmtBDT(r.lifetime_paid_commission + r.lifetime_cpa_paid)}
                  {r.lifetime_cpa_paid > 0 && (
                    <div className="text-[10px] text-amber-600">CPA: {fmtBDT(r.lifetime_cpa_paid)}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.custom_commission_pct != null ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 border border-purple-200 text-purple-700 rounded-sm font-semibold text-[11px]">
                      <Percent size={10} /> {Number(r.custom_commission_pct).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-[11px] text-gray-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex flex-wrap items-center justify-center gap-1">
                    {r.account_blocked && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-rose-100 border border-rose-300 text-rose-700 rounded-sm font-bold text-[10px]" title="Login blocked">🚫 Login</span>
                    )}
                    {r.balance_frozen && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-100 border border-blue-300 text-blue-700 rounded-sm font-bold text-[10px]" title="Balance frozen">❄ Bal</span>
                    )}
                    {r.payout_blocked && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 border border-amber-300 text-amber-700 rounded-sm font-bold text-[10px]" title="Payout blocked">⛔ Payout</span>
                    )}
                    {!r.account_blocked && !r.balance_frozen && !r.payout_blocked && (
                      <span className="inline-flex px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-sm font-semibold text-[10px]">Active</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex flex-wrap gap-1 justify-end">
                    <button
                      onClick={() => setEditing(r)}
                      className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 rounded-sm text-[11px] font-semibold inline-flex items-center gap-1"
                      data-testid={`aff-edit-${r.affiliate_id}`}
                      title="Edit (custom %, code, notes, controls)"
                    >
                      <Edit3 size={11} /> Edit
                    </button>
                    <button
                      onClick={() => quickToggle(r, "balance_frozen")}
                      className={`px-2 py-1 border rounded-sm text-[11px] font-semibold inline-flex items-center gap-1 ${r.balance_frozen ? "bg-blue-100 text-blue-800 border-blue-300" : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"}`}
                      data-testid={`aff-freeze-${r.affiliate_id}`}
                      title={r.balance_frozen ? "Click to UNFREEZE balance" : "Freeze balance (block all wagering)"}
                    >
                      ❄ {r.balance_frozen ? "Unfreeze" : "Freeze"}
                    </button>
                    <button
                      onClick={() => quickToggle(r, "payout_blocked")}
                      className={`px-2 py-1 border rounded-sm text-[11px] font-semibold inline-flex items-center gap-1 ${r.payout_blocked ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"}`}
                      data-testid={`aff-payout-${r.affiliate_id}`}
                      title={r.payout_blocked ? "Click to UNBLOCK payouts" : "Block payouts (reject withdrawals)"}
                    >
                      ⛔ {r.payout_blocked ? "Allow" : "Block"} Payout
                    </button>
                    <button
                      onClick={() => quickToggle(r, "account_blocked")}
                      className={`px-2 py-1 border rounded-sm text-[11px] font-semibold inline-flex items-center gap-1 ${r.account_blocked ? "bg-rose-100 text-rose-800 border-rose-300" : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"}`}
                      data-testid={`aff-block-${r.affiliate_id}`}
                      title={r.account_blocked ? "Click to UNBLOCK login" : "Block login (suspend account)"}
                    >
                      <Ban size={11} /> {r.account_blocked ? "Unblock" : "Block"}
                    </button>
                    <button
                      onClick={() => revoke(r)}
                      className="px-2 py-1 bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 rounded-sm text-[11px] font-semibold inline-flex items-center gap-1"
                      data-testid={`aff-revoke-${r.affiliate_id}`}
                      title="Revoke affiliate status (keeps user account)"
                    >
                      Revoke
                    </button>
                    <button
                      onClick={() => hardDelete(r)}
                      className="px-2 py-1 bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 rounded-sm text-[11px] font-semibold inline-flex items-center gap-1"
                      data-testid={`aff-delete-${r.affiliate_id}`}
                      title="PERMANENTLY delete user account (irreversible)"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          total={sortedRows.length}
          page={page}
          pageSize={PAGE_SIZE}
          onChange={setPage}
          testIdPrefix="aff-list-pg"
        />
      </div>

      {editing && <EditAffiliateModal aff={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function EditAffiliateModal({ aff, onClose, onSave }) {
  const [pct, setPct] = useState(aff.custom_commission_pct ?? "");
  const [code, setCode] = useState(aff.refer_code || "");
  const [promoCode, setPromoCode] = useState(aff.promo_code || "");
  const [notes, setNotes] = useState(aff.notes || "");
  const [blockLogin, setBlockLogin] = useState(!!aff.account_blocked);
  const [freezeBalance, setFreezeBalance] = useState(!!aff.balance_frozen);
  const [blockPayout, setBlockPayout] = useState(!!aff.payout_blocked);
  const [setBalanceMode, setSetBalanceMode] = useState(false);
  const [newBalance, setNewBalance] = useState("");

  const submit = () => {
    const payload = {};
    if (pct === "" || pct === null) {
      payload.clear_custom_pct = true;
    } else {
      const n = parseFloat(pct);
      if (Number.isNaN(n) || n < 0 || n > 100) return toast.error("Commission % must be 0–100");
      payload.custom_commission_pct = n;
    }
    if (code.trim() && code.trim().toUpperCase() !== (aff.refer_code || "")) {
      payload.refer_code = code.trim().toUpperCase();
    }
    const newPromo = promoCode.trim().toUpperCase();
    if (newPromo !== (aff.promo_code || "").toUpperCase()) {
      // Allow clearing (empty string) — backend unsets the field
      if (newPromo && !/^[A-Z0-9_-]{3,24}$/.test(newPromo)) {
        return toast.error("Promo code must be 3–24 chars (A-Z, 0-9, _ or -)");
      }
      payload.promo_code = newPromo;
    }
    if ((notes || "") !== (aff.notes || "")) payload.notes = notes;
    if (blockLogin !== !!aff.account_blocked) payload.block_login = blockLogin;
    if (freezeBalance !== !!aff.balance_frozen) payload.freeze_balance = freezeBalance;
    if (blockPayout !== !!aff.payout_blocked) payload.block_payout = blockPayout;
    if (setBalanceMode && newBalance !== "") {
      const b = parseFloat(newBalance);
      if (Number.isNaN(b) || b < 0) return toast.error("Balance must be ≥ 0");
      payload.set_balance = b;
    }
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="aff-edit-modal">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="font-semibold text-gray-900">Edit {aff.username}</div>
            <div className="text-xs text-gray-500 font-mono">{aff.refer_code} · Balance: ৳{Number(aff.balance || 0).toLocaleString("en-IN")}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-sm"><XCircle size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Risk / Control flags */}
          <div className="rounded-sm border border-rose-200 bg-rose-50/40 p-3 space-y-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-rose-700 flex items-center gap-1.5"><Ban size={12}/> Account Controls (fraud / abuse mitigation)</div>
            <label className="flex items-center justify-between text-[13px] py-1 cursor-pointer">
              <span><span className="font-semibold">Block login</span> <span className="text-gray-500">— affiliate cannot sign in</span></span>
              <input type="checkbox" className="w-4 h-4 accent-rose-600" checked={blockLogin} onChange={(e) => setBlockLogin(e.target.checked)} data-testid="aff-edit-block-login" />
            </label>
            <label className="flex items-center justify-between text-[13px] py-1 cursor-pointer">
              <span><span className="font-semibold">Freeze balance</span> <span className="text-gray-500">— cannot wager/play</span></span>
              <input type="checkbox" className="w-4 h-4 accent-rose-600" checked={freezeBalance} onChange={(e) => setFreezeBalance(e.target.checked)} data-testid="aff-edit-freeze-balance" />
            </label>
            <label className="flex items-center justify-between text-[13px] py-1 cursor-pointer">
              <span><span className="font-semibold">Block payout</span> <span className="text-gray-500">— withdrawals rejected</span></span>
              <input type="checkbox" className="w-4 h-4 accent-rose-600" checked={blockPayout} onChange={(e) => setBlockPayout(e.target.checked)} data-testid="aff-edit-block-payout" />
            </label>
            <label className="flex items-center gap-2 text-[13px] pt-1 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 accent-amber-600" checked={setBalanceMode} onChange={(e) => setSetBalanceMode(e.target.checked)} data-testid="aff-edit-setbal-toggle" />
              <span className="font-semibold">Force-set balance to:</span>
              <input type="number" min="0" step="0.01" disabled={!setBalanceMode} value={newBalance} onChange={(e) => setNewBalance(e.target.value)} placeholder="0" className="ml-2 h-8 w-32 px-2 border border-gray-300 rounded-sm text-sm disabled:bg-gray-100" data-testid="aff-edit-setbal-value" />
              <span className="text-gray-500 text-[11px]">৳</span>
            </label>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Custom commission % (leave blank for default/tier)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              placeholder="e.g., 45.0"
              className="w-full h-10 px-3 border border-gray-300 rounded-sm text-sm"
              data-testid="aff-edit-custom-pct"
            />
            <div className="text-[11px] text-gray-500 mt-1">This overrides the default rate and any tier the affiliate would otherwise fall into.</div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Referral code</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="w-full h-10 px-3 border border-gray-300 rounded-sm text-sm font-mono"
              data-testid="aff-edit-code"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-emerald-700 mb-1">
              Custom promo code <span className="text-gray-400 normal-case font-normal">— users register with this → 25% / 15-day cycle</span>
            </label>
            <input
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.replace(/[^A-Za-z0-9_-]/g, "").toUpperCase())}
              placeholder="e.g., BIGBOSS, GOLDVIP25 (3–24 chars, A-Z, 0-9, _ or -)"
              className="w-full h-10 px-3 border-2 border-emerald-300 bg-emerald-50/40 rounded-sm text-sm font-mono"
              data-testid="aff-edit-promo-code"
              maxLength={24}
            />
            <div className="text-[11px] text-gray-500 mt-1">
              When a new user types this code at registration, deposits/withdraws count toward this affiliate. Leave blank to disable. {aff.promo_code && <span className="text-emerald-700 font-semibold">· Current: <span className="font-mono">{aff.promo_code}</span></span>}
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Internal notes</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Admin-only note about this affiliate..."
              className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
              data-testid="aff-edit-notes"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-9 px-4 border border-gray-300 rounded-sm text-sm">Cancel</button>
          <button onClick={submit} className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-1.5" data-testid="aff-edit-save">
            <Save size={13} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================ Program Settings ============================ */
function ProgramSettings() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/affiliate-settings");
      setCfg(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/affiliate-settings", cfg);
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateTier = (idx, patch) => {
    const next = [...(cfg.affiliate_tiers || [])];
    next[idx] = { ...next[idx], ...patch };
    setCfg({ ...cfg, affiliate_tiers: next });
  };
  const addTier = () => {
    const next = [...(cfg.affiliate_tiers || [])];
    next.push({ id: `tier-${Date.now()}`, name: "New Tier", min_net_loss: 0, commission_pct: 10, color: "#64748b" });
    setCfg({ ...cfg, affiliate_tiers: next });
  };
  const removeTier = (idx) => {
    const next = (cfg.affiliate_tiers || []).filter((_, i) => i !== idx);
    setCfg({ ...cfg, affiliate_tiers: next });
  };

  if (loading || !cfg) return <div className="p-10 text-center text-gray-500 text-sm">Loading...</div>;

  return (
    <div className="space-y-5" data-testid="aff-settings-section">
      {/* General */}
      <Section title="General" icon={Settings}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Program Enabled">
            <Toggle
              checked={!!cfg.affiliate_enabled}
              onChange={(v) => setCfg({ ...cfg, affiliate_enabled: v })}
              testid="aff-enabled-toggle"
            />
          </Field>
          <Field label="Default commission %">
            <input type="number" step="0.1" min="0" max="100" value={cfg.affiliate_commission_pct}
              onChange={(e) => setCfg({ ...cfg, affiliate_commission_pct: parseFloat(e.target.value) || 0 })}
              className="w-full h-10 px-3 border border-gray-300 rounded-sm text-sm" data-testid="aff-default-pct" />
          </Field>
          <Field label="Payout period (days)">
            <input type="number" min="1" value={cfg.affiliate_period_days}
              onChange={(e) => setCfg({ ...cfg, affiliate_period_days: parseInt(e.target.value) || 1 })}
              className="w-full h-10 px-3 border border-gray-300 rounded-sm text-sm" data-testid="aff-period-days" />
          </Field>
          <Field label="Min payout ৳">
            <input type="number" step="1" min="0" value={cfg.affiliate_min_payout}
              onChange={(e) => setCfg({ ...cfg, affiliate_min_payout: parseFloat(e.target.value) || 0 })}
              className="w-full h-10 px-3 border border-gray-300 rounded-sm text-sm" data-testid="aff-min-payout" />
          </Field>
        </div>
      </Section>

      {/* Tiered commission */}
      <Section title="Tiered Commission (RevShare by Net Loss)" icon={Award}>
        <div className="flex items-center gap-3 mb-3">
          <Toggle
            checked={!!cfg.affiliate_tiers_enabled}
            onChange={(v) => setCfg({ ...cfg, affiliate_tiers_enabled: v })}
            testid="aff-tiers-enabled-toggle"
          />
          <span className="text-sm text-gray-600">
            When enabled, each affiliate's commission % is looked up from the table below using their period net-loss.
            Higher net-loss → higher tier → higher %.
          </span>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-[11px] uppercase tracking-wider text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left font-semibold w-12">Color</th>
                <th className="px-3 py-2 text-left font-semibold">Tier Name</th>
                <th className="px-3 py-2 text-right font-semibold">Min Net Loss ৳</th>
                <th className="px-3 py-2 text-right font-semibold">Commission %</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(cfg.affiliate_tiers || []).map((t, idx) => (
                <tr key={t.id || idx} className="bg-white">
                  <td className="px-3 py-2">
                    <input type="color" value={t.color || "#888"}
                      onChange={(e) => updateTier(idx, { color: e.target.value })}
                      className="h-8 w-10 border border-gray-200 rounded-sm" />
                  </td>
                  <td className="px-3 py-2">
                    <input value={t.name || ""} onChange={(e) => updateTier(idx, { name: e.target.value })}
                      className="w-full h-9 px-2 border border-gray-300 rounded-sm text-sm font-semibold"
                      data-testid={`aff-tier-name-${idx}`} />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" step="1" min="0" value={t.min_net_loss || 0}
                      onChange={(e) => updateTier(idx, { min_net_loss: parseFloat(e.target.value) || 0 })}
                      className="w-full h-9 px-2 border border-gray-300 rounded-sm text-sm tabular-nums text-right"
                      data-testid={`aff-tier-min-${idx}`} />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" step="0.1" min="0" max="100" value={t.commission_pct || 0}
                      onChange={(e) => updateTier(idx, { commission_pct: parseFloat(e.target.value) || 0 })}
                      className="w-full h-9 px-2 border border-gray-300 rounded-sm text-sm tabular-nums text-right font-bold text-emerald-700"
                      data-testid={`aff-tier-pct-${idx}`} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => removeTier(idx)}
                      className="p-1.5 rounded-sm hover:bg-rose-50 text-rose-500"
                      data-testid={`aff-tier-del-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {(cfg.affiliate_tiers || []).length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400 text-sm">No tiers defined — add one below.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <button type="button" onClick={addTier}
          className="mt-3 h-9 px-3 bg-gray-900 hover:bg-gray-800 text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5"
          data-testid="aff-tier-add">
          <Plus size={13} /> Add tier
        </button>
      </Section>

      {/* CPA bonus */}
      <Section title="CPA (Cost Per Acquisition) Bonus" icon={Gift}>
        <div className="flex items-center gap-3 mb-3">
          <Toggle
            checked={!!cfg.affiliate_cpa_enabled}
            onChange={(v) => setCfg({ ...cfg, affiliate_cpa_enabled: v })}
            testid="aff-cpa-enabled-toggle"
          />
          <span className="text-sm text-gray-600">
            When enabled, an affiliate receives a flat ৳ bonus the first time a referred user's lifetime deposits hit the threshold — paid instantly, in addition to the regular RevShare commission.
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="CPA bonus amount ৳ (per qualifying referral)">
            <input type="number" step="1" min="0" value={cfg.affiliate_cpa_bonus}
              onChange={(e) => setCfg({ ...cfg, affiliate_cpa_bonus: parseFloat(e.target.value) || 0 })}
              className="w-full h-10 px-3 border border-gray-300 rounded-sm text-sm tabular-nums"
              data-testid="aff-cpa-bonus" />
          </Field>
          <Field label="Min cumulative deposit ৳ to qualify">
            <input type="number" step="1" min="0" value={cfg.affiliate_cpa_min_deposit}
              onChange={(e) => setCfg({ ...cfg, affiliate_cpa_min_deposit: parseFloat(e.target.value) || 0 })}
              className="w-full h-10 px-3 border border-gray-300 rounded-sm text-sm tabular-nums"
              data-testid="aff-cpa-min-deposit" />
          </Field>
        </div>
      </Section>

      {/* Sub-affiliate (2-tier MLM) */}
      <Section title="Sub-Affiliate (2-Tier MLM Override)" icon={Users}>
        <div className="flex items-center gap-3 mb-3">
          <Toggle
            checked={!!cfg.affiliate_sub_enabled}
            onChange={(v) => setCfg({ ...cfg, affiliate_sub_enabled: v })}
            testid="aff-sub-enabled-toggle"
          />
          <span className="text-sm text-gray-600">
            When enabled, if Affiliate <b>A</b> refers a user who later becomes Affiliate <b>B</b>, then every time B earns RevShare commission, A automatically earns the override % below on top. Paid in the same period as B's commission.
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Upline override % (of downline's commission)">
            <input type="number" step="0.1" min="0" max="100" value={cfg.affiliate_sub_override_pct}
              onChange={(e) => setCfg({ ...cfg, affiliate_sub_override_pct: parseFloat(e.target.value) || 0 })}
              className="w-full h-10 px-3 border border-gray-300 rounded-sm text-sm tabular-nums font-bold text-purple-700"
              data-testid="aff-sub-override-pct" />
          </Field>
          <div className="text-xs bg-purple-50 border border-purple-200 rounded-sm p-3 text-purple-900 leading-relaxed">
            <b>Example:</b> B earns ৳3,000 RevShare. With <b>{Number(cfg.affiliate_sub_override_pct || 0).toFixed(1)}%</b> override, A automatically gets{" "}
            <b className="text-purple-700">৳{(3000 * Number(cfg.affiliate_sub_override_pct || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</b>{" "}
            as a separate pending commission record.
          </div>
        </div>
      </Section>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving}
          className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="aff-settings-save">
          <Save size={14} /> {saving ? "Saving…" : "Save all settings"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
        {Icon && <Icon size={14} className="text-emerald-500" />}
        <div className="text-[11px] uppercase tracking-widest text-gray-600 font-bold">{title}</div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${checked ? "bg-emerald-500" : "bg-gray-300"}`}
      data-testid={testid}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${checked ? "translate-x-5" : "translate-x-1"}`} />
    </button>
  );
}

/* ============================ Bits ============================ */
function StatusPill({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-700",
    approved: "bg-emerald-100 text-emerald-700",
    paid: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

function StatCard({ label, value, color = "blue", icon: Icon }) {
  const colorMap = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  };
  return (
    <div className={`rounded-sm border p-4 ${colorMap[color]}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest font-semibold opacity-80">{label}</span>
        {Icon && <Icon size={14} className="opacity-70" />}
      </div>
      <div className="text-xl font-bold mt-1 tabular-nums">{value}</div>
    </div>
  );
}
